/**
 * speed-widget.ts — top-bar speed control (v2M3).
 *
 * Four-button group: Pause / 1× / 2× / 4× per UI_UX §1.1. Highlights the
 * active state. Reads from a SpeedController; click handlers call into
 * the same controller. The widget is stateless — every update() re-reads
 * the controller and recolours the buttons.
 *
 * Mounted as a separate fixed element so the resource readout in hud.ts
 * stays untouched.
 */

import {
  USER_SPEED_LADDER,
  type SpeedController,
  type UserSpeed,
} from '../game/speed-controller';

export interface SpeedWidgetHandle {
  update(): void;
  destroy(): void;
}

/**
 * Visual states a button can be in:
 *   active  — currently selected (highlighted blue)
 *   normal  — selectable but not selected (HUD-surface bg)
 *
 * The active button is `Pause` when the controller reports `isPaused()`,
 * otherwise the speed button matching `getUserSpeed()`. Menu-pause does
 * NOT highlight the Pause button — menu-pause is a transient overlay
 * pause, not a player-chosen state, and the widget reflects player
 * intent.
 */

const ACTIVE_BG = '#2B4A7A';
const ACTIVE_BORDER = '#3F6FA8';
const ACTIVE_TEXT = '#FFFFFF';
const NORMAL_BG = '#0E131C';
const NORMAL_BORDER = '#2A3344';
const NORMAL_TEXT = '#D7DEEA';

export function mountSpeedWidget(
  parent: HTMLElement,
  controller: SpeedController,
): SpeedWidgetHandle {
  const root = document.createElement('div');
  root.id = 'hud-speed-widget';
  Object.assign(root.style, {
    position: 'fixed',
    top: '7px',
    right: '16px',
    height: '30px',
    display: 'inline-flex',
    alignItems: 'stretch',
    gap: '0',
    fontFamily:
      'Inter, system-ui, -apple-system, "Segoe UI", sans-serif',
    fontSize: '12px',
    letterSpacing: '0.04em',
    zIndex: '500', // below pickers (1100) and menu (1000), above canvas
  } as Partial<CSSStyleDeclaration>);

  function makeButton(label: string, onClick: () => void): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = label;
    Object.assign(btn.style, {
      padding: '0 12px',
      background: NORMAL_BG,
      color: NORMAL_TEXT,
      border: `1px solid ${NORMAL_BORDER}`,
      borderRadius: '0',
      fontFamily: 'inherit',
      fontSize: 'inherit',
      letterSpacing: 'inherit',
      cursor: 'pointer',
      minWidth: '40px',
    } as Partial<CSSStyleDeclaration>);
    btn.addEventListener('click', onClick);
    return btn;
  }

  const pauseBtn = makeButton('Pause', () => controller.toggleUserPause());
  const speedBtns = USER_SPEED_LADDER.map((s) =>
    makeButton(`${s}×`, () => controller.setUserSpeed(s)),
  );

  // First-button left rounding, last-button right rounding so the group
  // reads as a single segmented control.
  pauseBtn.style.borderTopLeftRadius = '2px';
  pauseBtn.style.borderBottomLeftRadius = '2px';
  speedBtns[speedBtns.length - 1]!.style.borderTopRightRadius = '2px';
  speedBtns[speedBtns.length - 1]!.style.borderBottomRightRadius = '2px';

  // Collapse adjacent borders so only one line shows between buttons.
  for (let i = 1; i < speedBtns.length; i++) {
    speedBtns[i]!.style.borderLeft = 'none';
  }
  speedBtns[0]!.style.borderLeft = 'none';

  root.appendChild(pauseBtn);
  for (const b of speedBtns) root.appendChild(b);

  function paint(btn: HTMLButtonElement, active: boolean): void {
    btn.style.background = active ? ACTIVE_BG : NORMAL_BG;
    btn.style.borderColor = active ? ACTIVE_BORDER : NORMAL_BORDER;
    btn.style.color = active ? ACTIVE_TEXT : NORMAL_TEXT;
    // Repaint the collapsed-borders by leaving non-leftmost speed buttons
    // borderLeft: 'none' regardless of state.
  }

  function refresh(): void {
    // The Pause button is highlighted only when the player chose pause —
    // not when an Esc-menu overlay is the cause. That keeps the widget
    // honest about player intent vs transient overlay state.
    const userPaused = controller.isUserPaused();
    paint(pauseBtn, userPaused);
    const current = controller.getUserSpeed();
    for (let i = 0; i < USER_SPEED_LADDER.length; i++) {
      const ladderSpeed: UserSpeed = USER_SPEED_LADDER[i]!;
      paint(speedBtns[i]!, !userPaused && current === ladderSpeed);
    }
    // Re-collapse the inner borders that paint() may have reset.
    for (let i = 1; i < speedBtns.length; i++) {
      speedBtns[i]!.style.borderLeft = 'none';
    }
    speedBtns[0]!.style.borderLeft = 'none';
  }

  refresh();
  parent.appendChild(root);

  return {
    update: refresh,
    destroy(): void {
      root.remove();
    },
  };
}
